import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { IconButton, Paper, Stack, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { TestBenchTestCard } from '@/api/models';
import { fontFamilyMono } from '@/theme/tokens';

interface TestCardPanelProps {
  testCard: TestBenchTestCard;
}

interface CopyableRowProps {
  label: string;
  value: string;
}

/** One labelled, monospace, copy-to-clipboard row — mirrors `CredentialSecretRevealPanel`'s copy pattern. */
function CopyableRow({ label, value }: CopyableRowProps) {
  const translate = useTranslate();
  const notify = useNotify();

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      notify(translate('pay.testBench.testCard.copied'), { type: 'info' });
    } catch {
      // Clipboard access can be denied by the browser sandbox; the value remains visible/selectable.
    }
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Typography variant="body2" sx={{ fontFamily: fontFamilyMono, fontWeight: 500 }}>
          {value}
        </Typography>
        <IconButton size="small" onClick={copy} aria-label={translate('pay.testBench.testCard.copy')}>
          <ContentCopyOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}

/**
 * Read-only "Tarjeta de prueba" panel (D4): the preset's fixed, non-secret
 * sandbox test card. Shown on the linked gateway account's detail page and
 * on the test-bench launch page, next to the launch action, whenever the
 * selected preset publishes one. Renders nothing when `testCard` is absent
 * at the call site — this component itself always assumes it is present.
 */
export function TestCardPanel({ testCard }: TestCardPanelProps) {
  const translate = useTranslate();

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.5, borderColor: 'divider', boxShadow: (theme) => theme.custom.cardShadow, p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {translate('pay.testBench.testCard.title')}
        </Typography>
        <CopyableRow label={translate('pay.testBench.testCard.fieldPan')} value={testCard.pan} />
        <CopyableRow label={translate('pay.testBench.testCard.fieldExpiry')} value={testCard.expiry} />
        <CopyableRow label={translate('pay.testBench.testCard.fieldCvv')} value={testCard.cvv} />
        {testCard.challengeCode && (
          <CopyableRow label={translate('pay.testBench.testCard.fieldChallengeCode')} value={testCard.challengeCode} />
        )}
        {testCard.notes && (
          <Typography variant="caption" color="text.secondary">
            {testCard.notes}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
